import type { components } from 'admination-react/api-schema'
import { ApiError, fillPath, getJson, sendForm } from '@/core/api/http'

type Schemas = components['schemas']

export const MULTIPART_FIELD = 'dateien'

export const GEVA_KLETT_DOKUMENT = { klett: "Klett-Test", geva: "GEVA-Test" } as const

export const GEVA_KLETT_DOKUMENTE: readonly string[] = Object.values(GEVA_KLETT_DOKUMENT)

export const GEVA_KLETT_ACCEPT: readonly string[] = ['application/pdf']

export const KLETT_BEREICHE = ['leseverstehen', 'hoerverstehen'] as const

export type KlettBereichName = (typeof KLETT_BEREICHE)[number]

export type KlettBereich = Schemas['KlettBereich']

export type KlettUploadResult = Pick<Schemas['KlettUploadResult'], 'niveau' | 'punkte' | 'punkte_max' | 'bereiche' | 'dokument_id'>

export type GevaMerkmal = Schemas['GevaMerkmal']

export const GEVA_ABSCHNITTE = [
  'schluesselqualifikationen',
  'berufliche_interessen',
  'berufliches_selbstvertrauen',
] as const


export type GevaDossier = Schemas['GevaDossier']

export type GevaUploadResult = Pick<Schemas['GevaUploadResult'], 'merkmale' | 'dossier' | 'dokument_id'>

export type GevaKlettStatus = Schemas['GevaKlettStandResult']

export type NamePruefung = 'pruefen' | 'bestaetigt'

export type NameAbweichung = { gelesen: string; teilnehmende: string }

export type GevaKlettFehlerZeile = {
  datei: string | null
  seite: number | null
  zeile: number | null
  feld: string | null
  grund: string
}

export type GevaKlettErrorBody = { meldung: string; fehler: GevaKlettFehlerZeile[] }

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

const isId = (value: unknown): value is string => typeof value === 'string' && value !== ''

const isIdOrNull = (value: unknown): value is string | null => value === null || isId(value)

function isKlettBereich(value: unknown): value is KlettBereich {
  return isRecord(value) && isNumber(value.punkte) && isNumber(value.punkte_max)
}

function zweiBereiche(bereiche: unknown): Record<KlettBereichName, KlettBereich> | null {
  if (!isRecord(bereiche) || Object.keys(bereiche).length !== KLETT_BEREICHE.length) return null
  const { leseverstehen, hoerverstehen } = bereiche
  return isKlettBereich(leseverstehen) && isKlettBereich(hoerverstehen) ? { leseverstehen, hoerverstehen } : null
}

export function klettUploadResult(body: unknown): KlettUploadResult | null {
  if (!isRecord(body)) return null
  const { niveau, punkte, punkte_max, dokument_id } = body
  const bereiche = zweiBereiche(body.bereiche)
  return typeof niveau === 'string' &&
    isNumber(punkte) &&
    isNumber(punkte_max) &&
    bereiche &&
    isId(dokument_id)
    ? { niveau, punkte, punkte_max, bereiche, dokument_id }
    : null
}

const isNumberOrNull = (value: unknown): value is number | null => value === null || isNumber(value)

function isGevaMerkmal(value: unknown): value is GevaMerkmal {
  if (!isRecord(value)) return false
  const { gruppe, merkmal, wert, vergleichsgruppe } = value
  return typeof gruppe === 'string' && typeof merkmal === 'string' && isNumberOrNull(wert) && isNumberOrNull(vergleichsgruppe)
}

export function isGevaDossier(body: unknown): body is GevaDossier {
  if (!isRecord(body)) return false
  const { name, teilnehmernummer, geburtsdatum, anmerkungen } = body
  return (
    typeof name === 'string' &&
    typeof teilnehmernummer === 'string' &&
    typeof geburtsdatum === 'string' &&
    typeof anmerkungen === 'string' &&
    GEVA_ABSCHNITTE.every((key) => {
      const merkmale = body[key]
      return Array.isArray(merkmale) && merkmale.every(isGevaMerkmal)
    })
  )
}

export function gevaUploadResult(body: unknown): GevaUploadResult | null {
  if (!isRecord(body)) return null
  const { merkmale, dossier, dokument_id } = body
  return isNumber(merkmale) && isGevaDossier(dossier) && isId(dokument_id)
    ? { merkmale, dossier, dokument_id }
    : null
}

export function isGevaKlettStatus(body: unknown): body is GevaKlettStatus {
  if (!isRecord(body)) return false
  const { klett, geva } = body
  if (!isRecord(klett) || !isRecord(geva)) return false
  return (
    typeof klett.fertig === 'boolean' &&
    (klett.niveau === null || typeof klett.niveau === 'string') &&
    (klett.punkte === null || isNumber(klett.punkte)) &&
    isNumberOrNull(klett.punkte_max) &&
    (klett.bereiche === null || zweiBereiche(klett.bereiche) !== null) &&
    isIdOrNull(klett.dokument_id) &&
    typeof geva.fertig === 'boolean' &&
    (geva.merkmale === null || isNumber(geva.merkmale)) &&
    (geva.dossier === null || isGevaDossier(geva.dossier)) &&
    isIdOrNull(geva.dokument_id)
  )
}

function isFehlerZeile(item: unknown): item is GevaKlettFehlerZeile {
  if (!isRecord(item)) return false
  const { datei, seite, zeile, feld, grund } = item
  return (
    (datei === null || typeof datei === 'string') &&
    (seite === null || isNumber(seite)) &&
    (zeile === null || isNumber(zeile)) &&
    (feld === null || typeof feld === 'string') &&
    typeof grund === 'string'
  )
}

export function gevaKlettErrorBody(error: unknown): GevaKlettErrorBody | null {
  if (!(error instanceof ApiError) || !isRecord(error.detail)) return null
  const body = error.detail.detail
  if (!isRecord(body) || typeof body.meldung !== 'string' || body.meldung.trim() === '') return null
  const fehler = body.fehler ?? []
  if (!Array.isArray(fehler) || !fehler.every(isFehlerZeile)) return null
  return { meldung: body.meldung, fehler }
}

export function nameAbweichung(error: unknown): NameAbweichung | null {
  if (!(error instanceof ApiError) || error.status !== 409 || !isRecord(error.detail)) return null
  const body = error.detail.detail
  if (!isRecord(body) || body.code !== 'name_abweichend') return null
  const { gelesen, teilnehmende } = body
  return typeof gelesen === 'string' && typeof teilnehmende === 'string' ? { gelesen, teilnehmende } : null
}

const MALFORMED = "Unexpected GEVA/Klett response shape"

function uploadForm(file: File, name: NamePruefung): FormData {
  const form = new FormData()
  form.append(MULTIPART_FIELD, file, file.name)
  form.append('name_pruefen', 'true')
  if (name === 'bestaetigt') form.append('name_bestaetigt', 'true')
  return form
}

export async function uploadKlett(coachingId: string, file: File, name: NamePruefung): Promise<KlettUploadResult> {
  const body = await sendForm<unknown>(
    fillPath('/geva-klett/{coaching_id}/klett', { coaching_id: coachingId }),
    uploadForm(file, name),
  )
  const result = klettUploadResult(body)
  if (!result) throw new Error(MALFORMED)
  return result
}

export async function uploadGeva(coachingId: string, file: File, name: NamePruefung): Promise<GevaUploadResult> {
  const body = await sendForm<unknown>(
    fillPath('/geva-klett/{coaching_id}/geva', { coaching_id: coachingId }),
    uploadForm(file, name),
  )
  const result = gevaUploadResult(body)
  if (!result) throw new Error(MALFORMED)
  return result
}

export async function getGevaKlettStatus(coachingId: string): Promise<GevaKlettStatus> {
  const body = await getJson<unknown>(fillPath('/geva-klett/{coaching_id}', { coaching_id: coachingId }))
  if (!isGevaKlettStatus(body)) throw new Error(MALFORMED)
  return body
}
